// src/pages/Auth/SignupPage.jsx
import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminPanelHeader from '../../components/common/Header/AdminPanelHeader';
import Footer from '../../components/common/Footer/Footer';
// Import SiriDharini logo
import logoImage from '../../assets/images/siridharinilogo.jpeg';
import { API_BASE_URL, API_KEY } from '../../config/api';

const AdminSignupPage = () => {
    const navigate = useNavigate();

    // API base URL - Using SiriDharini admin endpoints from centralized config
    

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        agreeToTerms: false
    });
    
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [validationState, setValidationState] = useState({
        username: { checking: false, available: null }
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Reset validation state for username
        if (name === 'username') {
            setValidationState(prev => ({
                ...prev,
                username: { checking: false, available: null }
            }));
        }
    };

    // Debounced function for API calls
    const debounce = useCallback((func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    }, []);

    // Check username availability - Updated for SiriDharini endpoints
    const checkUsernameAvailability = useCallback(async (username) => {
        if (!username || username.length < 3) {
            setValidationState(prev => ({
                ...prev,
                username: { checking: false, available: null }
            }));
            return;
        }
        
        setValidationState(prev => ({
            ...prev,
            username: { checking: true, available: null }
        }));

        try {
            console.log('Checking SiriDharini admin username availability:', username);
            const response = await fetch(`${API_BASE_URL}/siridharini/admin/check-username/${encodeURIComponent(username)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': API_KEY
                }
            });
            
            const data = await response.json();
            console.log('Username check response:', data);
            
            if (response.ok) {
                setValidationState(prev => ({
                    ...prev,
                    username: { checking: false, available: data.available }
                }));

                if (!data.available) {
                    setErrors(prev => ({
                        ...prev,
                        username: data.message || 'Username is already taken'
                    }));
                } else {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.username;
                        return newErrors;
                    });
                }
            } else {
                setValidationState(prev => ({
                    ...prev,
                    username: { checking: false, available: false }
                }));
                setErrors(prev => ({
                    ...prev,
                    username: data.message || 'Error checking username availability'
                }));
            }
        } catch (error) {
            console.error('Error checking username:', error);
            setValidationState(prev => ({
                ...prev,
                username: { checking: false, available: null }
            }));
        }
    }, [API_BASE_URL]);

    // Debounced version
    const debouncedUsernameCheck = useCallback(debounce(checkUsernameAvailability, 500), [checkUsernameAvailability, debounce]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            newErrors.username = 'Username must be at least 3 characters long';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters long';
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!formData.agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Check if username is still being validated
        if (validationState.username.checking) {
            setErrors({ general: 'Please wait while we validate your information...' });
            return;
        }

        // Check if username is not available
        if (validationState.username.available === false) {
            setErrors({ general: 'Please fix the validation errors before proceeding.' });
            return;
        }

        setIsLoading(true);

        try {
            console.log('Submitting SiriDharini admin signup request...');
            console.log('Request URL:', `${API_BASE_URL}/siridharini/admin/signup`);
            
            const response = await fetch(`${API_BASE_URL}/siridharini/admin/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': API_KEY
                },
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password
                })
            });

            const data = await response.json();

            if (data.success) {
                console.log('SiriDharini admin signup successful:', data.message);
                // Redirect to login page with success message
                navigate('/admin', { 
                    state: { 
                        message: 'Admin account created successfully. Please login with your credentials.' 
                    } 
                });
            } else {
                setErrors({ 
                    general: data.message || 'Failed to create admin account.' 
                });
            }
            
        } catch (error) {
            console.error('Signup error:', error);
            setErrors({ 
                general: error.message || 'Network error. Please check your connection and try again.' 
            });
        } finally {
            setIsLoading(false);
        }
    };

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff'
        },
        container: {
            flex: 1,
            background: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1rem',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            minHeight: '70vh'
        },
        signupCard: {
            width: '100%',
            maxWidth: '28rem',
            background: '#ffffff',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb'
        },
        // Logo Image - Simple, no card styling
        logoImageDirect: {
            textAlign: 'center',
            marginBottom: '1rem'
        },
        logoImageSimple: {
            width: '4rem',
            height: '4rem',
            borderRadius: '12px',
            objectFit: 'contain'
        },
        // Fallback logo - Simple
        logoFallbackSimple: {
            width: '4rem',
            height: '4rem',
            background: '#22c55e',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            margin: '0 auto'
        },
        // Logo text section - No borders or card styling
        logoTextSection: {
            textAlign: 'center',
            marginBottom: '1.5rem'
        },
        companyName: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#000000',
            marginBottom: '0.5rem',
            lineHeight: '1.2'
        },
        tagline: {
            color: '#6b7280',
            fontSize: '0.875rem',
            marginBottom: '0.25rem',
            lineHeight: '1.3'
        },
        distributor: {
            color: '#9ca3af',
            fontSize: '0.75rem',
            fontWeight: '500'
        },
        welcomeSection: {
            textAlign: 'center',
            marginBottom: '1.5rem'
        },
        welcomeTitle: {
            fontSize: '1.875rem',
            fontWeight: 'bold',
            color: '#1a1a1a',
            marginBottom: '0.5rem',
            lineHeight: '1.2'
        },
        welcomeSubtitle: {
            fontSize: '1rem',
            color: '#6b7280'
        },
        form: {
            marginBottom: '1rem'
        },
        formGroup: {
            marginBottom: '1.5rem'
        },
        label: {
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '0.5rem'
        },
        inputContainer: {
            position: 'relative'
        },
        input: {
            width: '100%',
            padding: '0.75rem 1rem',
            background: '#f9fafb',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '1rem',
            transition: 'all 0.2s ease',
            outline: 'none',
            boxSizing: 'border-box'
        },
        inputSuccess: {
            borderColor: '#22c55e',
            background: '#f0fdf4'
        },
        inputError: {
            borderColor: '#ef4444',
            background: '#fef2f2'
        },
        inputIcon: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            fontSize: '1.25rem'
        },
        validationIcon: {
            position: 'absolute',
            right: '2.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '1rem'
        },
        passwordToggle: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            padding: '0.25rem',
            transition: 'color 0.2s ease'
        },
        errorMessage: {
            display: 'flex',
            alignItems: 'center',
            color: '#ef4444',
            fontSize: '0.875rem',
            marginTop: '0.5rem'
        },
        successMessage: {
            display: 'flex',
            alignItems: 'center',
            color: '#22c55e',
            fontSize: '0.875rem',
            marginTop: '0.5rem'
        },
        errorIcon: {
            width: '1rem',
            height: '1rem',
            marginRight: '0.5rem'
        },
        checkboxContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            cursor: 'pointer',
            marginBottom: '1.5rem'
        },
        checkboxWrapper: {
            position: 'relative',
            marginRight: '0.75rem',
            marginTop: '0.125rem'
        },
        checkbox: {
            position: 'absolute',
            opacity: 0,
            cursor: 'pointer'
        },
        checkboxCustom: {
            width: '1.25rem',
            height: '1.25rem',
            border: '2px solid #d1d5db',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
        },
        checkboxChecked: {
            background: '#22c55e',
            borderColor: '#22c55e'
        },
        checkboxLabel: {
            fontSize: '0.875rem',
            color: '#4b5563',
            lineHeight: '1.4'
        },
        termsLink: {
            color: '#22c55e',
            textDecoration: 'none',
            fontWeight: '500'
        },
        signupButton: {
            width: '100%',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            fontWeight: '600',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#22c55e'
        },
        signupButtonLoading: {
            background: '#9ca3af',
            cursor: 'not-allowed'
        },
        spinner: {
            width: '1.25rem',
            height: '1.25rem',
            border: '2px solid #ffffff',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginRight: '0.75rem'
        },
        footer: {
            textAlign: 'center',
            paddingTop: '1rem',
            borderTop: '1px solid #f3f4f6'
        },
        footerItem: {
            marginBottom: '0.75rem'
        },
        footerText: {
            fontSize: '0.875rem',
            color: '#6b7280'
        },
        footerLink: {
            fontSize: '0.875rem',
            color: '#22c55e',
            textDecoration: 'none',
            fontWeight: '500',
            transition: 'color 0.2s ease'
        },
        securityBadge: {
            paddingTop: '1rem',
            borderTop: '1px solid #f3f4f6',
            marginTop: '0.75rem'
        },
        securityContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#6b7280'
        },
        securityIcon: {
            width: '1rem',
            height: '1rem',
            color: '#22c55e',
            marginRight: '0.5rem'
        }
    };

    return (
        <>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .signup-input:focus {
                    border-color: #22c55e !important;
                    background: #ffffff !important;
                    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1) !important;
                }

                .signup-input.error {
                    border-color: #ef4444 !important;
                    background: #fef2f2 !important;
                }

                .signup-input.success {
                    border-color: #22c55e !important;
                    background: #f0fdf4 !important;
                }

                .signup-button:hover:not(:disabled) {
                    background: #16a34a !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 6px -1px rgba(34, 197, 94, 0.4) !important;
                }

                .footer-link:hover {
                    color: #16a34a !important;
                    text-decoration: underline !important;
                }

                .terms-link:hover {
                    color: #16a34a !important;
                    text-decoration: underline !important;
                }

                .password-toggle:hover {
                    color: #4b5563 !important;
                }

                @media (max-width: 768px) {
                    .signup-card {
                        margin: 1rem;
                        padding: 1.5rem;
                    }
                    
                    .welcome-title {
                        font-size: 1.5rem !important;
                    }
                }
                
                @media (max-width: 640px) {
                    .container {
                        padding: 1rem 0.5rem;
                    }
                    
                    .signup-card {
                        padding: 1rem;
                    }
                    
                    .welcome-title {
                        font-size: 1.25rem !important;
                    }
                }
            `}</style>

            <div style={styles.pageContainer}>
                <AdminPanelHeader/>

                <div style={styles.container}>
                    <div style={styles.signupCard} className="signup-card">
                        {/* Logo Image - Directly in card, no container/card styling */}
                        <div style={styles.logoImageDirect}>
                            <img 
                                src={logoImage} 
                                alt="SiriDharini Logo"
                                style={styles.logoImageSimple}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div style={{
                                ...styles.logoFallbackSimple,
                                display: 'none' // Will show if image fails to load
                            }}>
                                S
                            </div>
                        </div>

                        {/* Logo Text Section - No border, just text */}
                        <div style={styles.logoTextSection}>
                            <h2 style={styles.companyName}>SiriDharini</h2>
                            <p style={styles.tagline}>Premium Quality Organic Products</p>
                            <p style={styles.distributor}>Natural & Sustainable Farming</p>
                        </div>

                        <div style={styles.welcomeSection}>
                            <h1 style={styles.welcomeTitle} className="welcome-title">Create Admin Account</h1>
                            <p style={styles.welcomeSubtitle}>Join our platform and manage SiriDharini</p>
                        </div>

                        {errors.general && (
                            <div style={{...styles.errorMessage, marginBottom: '1rem', justifyContent: 'center'}}>
                                <span style={styles.errorIcon}>⚠️</span>
                                {errors.general}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} style={styles.form}>
                            {/* Username Field */}
                            <div style={styles.formGroup}>
                                <label htmlFor="username" style={styles.label}>Username</label>
                                <div style={styles.inputContainer}>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        onBlur={(e) => debouncedUsernameCheck(e.target.value)}
                                        disabled={isLoading}
                                        className={`signup-input ${errors.username ? 'error' : validationState.username.available === true ? 'success' : ''}`}
                                        style={{
                                            ...styles.input,
                                            ...(errors.username ? styles.inputError : validationState.username.available === true ? styles.inputSuccess : {})
                                        }}
                                        placeholder="Choose a username"
                                    />
                                    <div style={styles.validationIcon}>
                                        {validationState.username.checking ? '⏳' : 
                                         validationState.username.available === true ? '✅' : 
                                         validationState.username.available === false ? '❌' : ''}
                                    </div>
                                    <div style={styles.inputIcon}>🆔</div>
                                </div>
                                {errors.username && (
                                    <div style={styles.errorMessage}>
                                        <span style={styles.errorIcon}>⚠️</span>
                                        {errors.username}
                                    </div>
                                )}
                                {validationState.username.available === true && !errors.username && (
                                    <div style={styles.successMessage}>
                                        <span style={styles.errorIcon}>✅</span>
                                        Username is available
                                    </div>
                                )}
                            </div>

                            {/* Password Field */}
                            <div style={styles.formGroup}>
                                <label htmlFor="password" style={styles.label}>Password</label>
                                <div style={styles.inputContainer}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className={`signup-input ${errors.password ? 'error' : ''}`}
                                        style={{
                                            ...styles.input,
                                            paddingRight: '3rem',
                                            ...(errors.password ? styles.inputError : {})
                                        }}
                                        placeholder="Create password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={isLoading}
                                        className="password-toggle"
                                        style={styles.passwordToggle}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div style={styles.errorMessage}>
                                        <span style={styles.errorIcon}>⚠️</span>
                                        {errors.password}
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password Field */}
                            <div style={styles.formGroup}>
                                <label htmlFor="confirmPassword" style={styles.label}>Confirm Password</label>
                                <div style={styles.inputContainer}>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        className={`signup-input ${errors.confirmPassword ? 'error' : ''}`}
                                        style={{
                                            ...styles.input,
                                            paddingRight: '3rem',
                                            ...(errors.confirmPassword ? styles.inputError : {})
                                        }}
                                        placeholder="Confirm password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        disabled={isLoading}
                                        className="password-toggle"
                                        style={styles.passwordToggle}
                                    >
                                        {showConfirmPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <div style={styles.errorMessage}>
                                        <span style={styles.errorIcon}>⚠️</span>
                                        {errors.confirmPassword}
                                    </div>
                                )}
                            </div>

                            {/* Terms and Conditions */}
                            <label style={styles.checkboxContainer}>
                                <div style={styles.checkboxWrapper}>
                                    <input
                                        type="checkbox"
                                        name="agreeToTerms"
                                        checked={formData.agreeToTerms}
                                        onChange={handleInputChange}
                                        disabled={isLoading}
                                        style={styles.checkbox}
                                    />
                                    <div style={{
                                        ...styles.checkboxCustom,
                                        ...(formData.agreeToTerms ? styles.checkboxChecked : {}),
                                        ...(errors.agreeToTerms ? { borderColor: '#ef4444' } : {})
                                    }}>
                                        {formData.agreeToTerms && <span style={{ color: 'white', fontSize: '0.75rem' }}>✓</span>}
                                    </div>
                                </div>
                                <span style={styles.checkboxLabel}>
                                    I agree to the{' '}
                                    <Link to="/terms" className="terms-link" style={styles.termsLink}>
                                        Terms and Conditions
                                    </Link>
                                    {' '}and{' '}
                                    <Link to="/privacy" className="terms-link" style={styles.termsLink}>
                                        Privacy Policy
                                    </Link>
                                </span>
                            </label>
                            {errors.agreeToTerms && (
                                <div style={{...styles.errorMessage, marginTop: '-1rem', marginBottom: '1rem'}}>
                                    <span style={styles.errorIcon}>⚠️</span>
                                    {errors.agreeToTerms}
                                </div>
                            )}

                            {/* Signup Button */}
                            <button
                                type="submit"
                                disabled={isLoading || validationState.username.checking}
                                className="signup-button"
                                style={{
                                    ...styles.signupButton,
                                    ...(isLoading || validationState.username.checking ? styles.signupButtonLoading : {})
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <div style={styles.spinner}></div>
                                        Creating Account...
                                    </>
                                ) : validationState.username.checking ? (
                                    <>
                                        <div style={styles.spinner}></div>
                                        Validating...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </button>
                        </form>

                        {/* Footer */}
                        <div style={styles.footer}>
                            <div style={styles.footerItem}>
                                <span style={styles.footerText}>Already have an account? </span>
                                <Link to="/admin" className="footer-link" style={styles.footerLink}>
                                    Sign in
                                </Link>
                            </div>

                            {/* Security Badge */}
                            <div style={styles.securityBadge}>
                                <div style={styles.securityContent}>
                                    <span style={styles.securityIcon}>🔒</span>
                                    <span>Secured by 256-bit SSL encryption</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default AdminSignupPage;